import { Controller } from "@hotwired/stimulus";
import axios from "axios";
import { Modal } from "bootstrap";
import debounce from "debounce";

export default class extends Controller {
  QRCodeStyling = null;
  isModalLoaded = false;
  isModalLoading = false;
  currentStepModalData = 1;
  totalStepCountModalData = 1;
  entriesToDelete = [];

  static targets = [
    "modalBody",
    "cleanupSize",
    "cleanupErr",
    "modalSearch",
    "completeCleanupBtn",
    "cleanupInfo",
    "reductionInfo",
    "reductionSize",
    "cancelCleanupBtn",
  ];

  static values = {
    notFound: String,
    mode: String, // "cleanup" or "reduction"
    paymentLink: String,
    needToCleanMb: Number,
  };

  async connect() {
    this.QRCodeStyling = (await import("qr-code-styler")).default;
    this.modalBodyTarget.addEventListener("click", this.qrItemClickHandler);
    this.checkModalScroll();
    this.searchQr = await debounce(this.searchQr.bind(this), 1000);
  }

  async onOpen() {
    if (!this.isModalLoaded) {
      await this.loadModalContent();
    }
  }

  beforeOpen() {
    if (this.modeValue === "cleanup") {
      this.cancelCleanupBtnTarget.innerText = this.cancelCleanupBtnTarget.dataset.cleanupTitle;
      this.completeCleanupBtnTarget.innerText = this.completeCleanupBtnTarget.dataset.cleanupTitle;
      this.cleanupInfoTarget.classList.remove("d-none");
      this.reductionInfoTarget.classList.add("d-none");
    } else if (this.modeValue === "reduction") {
      this.cancelCleanupBtnTarget.innerText = this.cancelCleanupBtnTarget.dataset.reductionTitle;
      this.completeCleanupBtnTarget.innerText = this.completeCleanupBtnTarget.dataset.reductionTitle;
      this.completeCleanupBtnTarget.classList.add("disabled");
      this.cleanupInfoTarget.classList.add("d-none");
      this.reductionInfoTarget.classList.remove("d-none");
      this.reductionSizeTarget.innerText = `${this.needToCleanMbValue.toFixed(
        2
      )} MB`;
    }
  }

  afterClose() {
    const modalEl = document.getElementById("cleanupStorage");
    const controller = this.application.getControllerForElementAndIdentifier(
      modalEl,
      "payment--cleanup-modal"
    );
    controller.modeValue = "cleanup";
    controller.paymentLinkValue = "";
    controller.needToCleanMbValue = "";
    this.cleanupInfoTarget.classList.remove("d-none");
    this.reductionInfoTarget.classList.add("d-none");
    this.reductionSizeTarget.innerText = "";
    this.completeCleanupBtnTarget.classList.remove("disabled");
    this.cancelCleanupBtnTarget.innerText = this.cancelCleanupBtnTarget.dataset.cleanupTitle;
    this.completeCleanupBtnTarget.innerText = this.completeCleanupBtnTarget.dataset.cleanupTitle;
  }

  async loadModalContent({ searchParam = "" } = {}) {
    this.isModalLoading = true;
    try {
      const { data } = await axios(
        `/ajax/entry/user-entry-data?revert_result=1&step=${
          this.currentStepModalData
        }&add_file_size=1&file_type_filter=1${
          searchParam ? `&entry_title=${searchParam}` : ""
        }`
      );
      this.modalSearchTarget.classList.remove("d-none");

      if (this.currentStepModalData === 1 || !this.isModalLoaded) {
        this.modalBodyTarget.innerHTML = "";
      }
      this.currentStepModalData += 1;
      this.totalStepCountModalData = data.pagination["totalStepCount"];

      if (!data.data.length) {
        this.modalBodyTarget.innerHTML = `<div class="text-center my-5 text-gray">${this.notFoundValue}</div>`;
        return;
      }
      data.data.forEach((item) => {
        this.modalBodyTarget.innerHTML += `<div
            class="qr-cleanup-item form-check border rounded-1 mb-3 p-2 d-flex align-items-center"
            data-qr-id='${item.id}'
          >
            <label class="form-check-label ps-3" for="radioCleanup${item.id}">
              <div class="row align-items-center">
                <div class="col-1">
                  <input
                    class="form-check-input m-0 p-0 pe-none"
                    type="checkbox"
                    name="radioCleanup"
                    id="radioCleanup${item.id}"
                    readonly
                  >
                </div>
                <div class="col-3">
                  ${
                    item.image
                      ? `<div class="d-flex justify-content-center align-items-center h-100"">
                          <img src="${item.image}" class="w-100">
                        </div>`
                      : `<div
                          class="qrcode d-flex justify-content-center align-items-center h-100"
                          data-link="${item.link}"
                        ></div>`
                  }
                </div>
                <div class="col-8">
                  <div class="fw-bold">${item.title}</div>
                    <div class="overflow-hidden">
                      <span class="text-gray me-2">URL:</span>
                      <span>${item.link}</span>
                    </div>
                    <div class="overflow-hidden">
                      <span class="text-gray me-2">Size:</span>
                      <span class="size-mb">${parseFloat(
                        (item.file_size / 1024 / 1024).toFixed(2)
                      )}</span> <span>MB</span>
                    </div>
                </div>
              </div>
            </label>
        </div>`;
      });

      this.modalBodyTarget.querySelectorAll(".qrcode").forEach((item) => {
        item.innerHTML = "";
        let newQr = new this.QRCodeStyling({ data: item.dataset.link });
        newQr.append(item);
      });

      this.isModalLoaded = true;
    } catch (err) {
    } finally {
      this.loadingSpinnerShow(false);
      this.isModalLoading = false;
    }
  }
  qrItemClickHandler = (e) => {
    const qrItem = e.target.closest(".qr-cleanup-item");
    if (!qrItem) return;

    const input = qrItem.querySelector("input");
    if (!input.checked) {
      qrItem.classList.remove("border-primary");
      this.entriesToDelete = this.entriesToDelete.filter(
        (id) => id !== qrItem.dataset.qrId
      );
    } else if (!this.entriesToDelete.includes(qrItem.dataset.qrId)) {
      qrItem.classList.add("border-primary");
      this.entriesToDelete.push(qrItem.dataset.qrId);
    }
    const cleanupSize = this.calculateCleanupSize();
    this.cleanupSizeTarget.textContent = cleanupSize;

    if (cleanupSize > this.needToCleanMbValue) {
      this.completeCleanupBtnTarget.classList.remove("disabled");
    } else {
      this.completeCleanupBtnTarget.classList.add("disabled");
    }
  };

  calculateCleanupSize() {
    const checkedQrItems =
      this.modalBodyTarget.querySelectorAll(".border-primary");
    let totalSize = 0;
    checkedQrItems.forEach((item) => {
      totalSize += Number(item.querySelector(".size-mb").textContent);
    });
    return parseFloat(totalSize).toFixed(2);
  }

  loadingSpinnerShow(isShow) {
    const modalContent = this.modalBodyTarget;
    let newDiv = document.createElement("div");
    newDiv.innerHTML = `<div class="text-center" id="loadingSpinner">
        <div class="spinner-border text-gray" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>`;
    if (isShow) {
      modalContent.appendChild(newDiv);
    } else {
      const loadingSpinner = modalContent.querySelector("#loadingSpinner");
      loadingSpinner?.parentNode.removeChild(loadingSpinner);
    }
  }

  selectAll() {
    const qrItems = Array.from(
      this.modalBodyTarget.querySelectorAll(".qr-cleanup-item")
    );

    if (qrItems.length === 0) return;

    const allChecked = qrItems.every((item) =>
      item.classList.contains("border-primary")
    );

    qrItems.forEach((qrItem) => {
      const input = qrItem.querySelector("input");
      input.checked = !allChecked;
      this.qrItemClickHandler({ target: qrItem });
    });
  }

  checkModalScroll() {
    const modalContent = this.modalBodyTarget;
    modalContent.addEventListener("scroll", () => {
      if (
        !this.isModalLoading &&
        this.currentStepModalData <= this.totalStepCountModalData &&
        modalContent.scrollHeight - modalContent.scrollTop <=
          modalContent.clientHeight + 100
      ) {
        this.loadingSpinnerShow(true);
        this.loadModalContent();
      }
    });
  }

  async completeCleanup(e) {
    if (!this.entriesToDelete.length) return;

    const button = event.currentTarget;
    button.disabled = true;
    try {
      await axios.post("/ajax/entry/delete-entries", {
        entries: this.entriesToDelete,
      });

      if (this.modeValue === "cleanup") {
        window.location.reload();
      } else if (this.modeValue === "reduction") {
        window.location.href = this.paymentLinkValue;
      }

      this.resetModal();
      const modalElement = document.getElementById("cleanupStorage");
      const modalInstance = Modal.getInstance(modalElement);
      modalInstance.hide();
    } catch (err) {
      this.showErrorMsg(this.cleanupErrTarget);
    } finally {
      button.disabled = false;
    }
  }

  resetModal() {
    this.isModalLoaded = false;
    this.isModalLoading = false;
    this.currentStepModalData = 1;
    this.totalStepCountModalData = 1;
    this.entriesToDelete = [];
    this.cleanupSizeTarget.textContent = "0";

    // Очищаем содержимое модалки
    this.modalBodyTarget.innerHTML = `
        <div class="text-center my-5">
          <div class="spinner-border text-gray" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      `;
  }

  showErrorMsg(target) {
    target.classList.remove("d-none");
    setTimeout(() => target.classList.add("d-none"), 5000);
  }

  async searchQr(e) {
    this.currentStepModalData = 1;
    this.totalStepCountModalData = 1;
    await this.loadModalContent({
      searchParam: e.target.value,
    });
  }
}
